import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  Copy,
  Check,
  Sparkles,
  MessageCircle,
  Facebook,
  Send,
  Share2,
} from "lucide-react";
import {
  FacebookShareButton,
  WhatsappShareButton,
  TelegramShareButton,
  TwitterShareButton,
} from "react-share";
import { useTranslation } from "react-i18next";

interface ShareModalProps {
  url: string;
  title?: string;
  description?: string;
  mediaUrl?: string;
  renderTrigger: (open: () => void) => React.ReactNode;
}

const SHARE_PLATFORMS = [
  {
    id: "whatsapp",
    nameAr: "واتساب",
    nameEn: "WhatsApp",
    nameKu: "واتسئاپ",
    color: "from-emerald-400 to-green-500",
    bg: "bg-emerald-50",
    iconColor: "text-emerald-600",
    icon: MessageCircle,
  },
  {
    id: "facebook",
    nameAr: "فيسبوك",
    nameEn: "Facebook",
    nameKu: "فەیسبووک",
    color: "from-blue-500 to-blue-600",
    bg: "bg-blue-50",
    iconColor: "text-blue-600",
    icon: Facebook,
  },
  {
    id: "telegram",
    nameAr: "تيليجرام",
    nameEn: "Telegram",
    nameKu: "تێلێگرام",
    color: "from-sky-400 to-blue-500",
    bg: "bg-sky-50",
    iconColor: "text-sky-600",
    icon: Send,
  },
  {
    id: "twitter",
    nameAr: "اكس",
    nameEn: "X / Twitter",
    nameKu: "ئێکس",
    color: "from-gray-800 to-black",
    bg: "bg-gray-100",
    iconColor: "text-gray-800",
    icon: X,
  },
];

export const ShareModal = ({
  url,
  title,
  description,
  mediaUrl,
  renderTrigger,
}: ShareModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [justShared, setJustShared] = useState<string | null>(null);
  const { i18n } = useTranslation();
  const locale = i18n.language;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Update meta tags when sharing a product
  useEffect(() => {
    if (!isOpen) return;

    const updateMetaTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("property", property);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    const updateNameMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    if (title) {
      updateMetaTag("og:title", title);
      updateNameMetaTag("twitter:title", title);
    }
    if (description) {
      updateMetaTag("og:description", description);
      updateNameMetaTag("twitter:description", description);
    }
    if (mediaUrl) {
      updateMetaTag("og:image", mediaUrl);
      updateNameMetaTag("twitter:image", mediaUrl);
    }
    updateMetaTag("og:type", "product");
    updateNameMetaTag("twitter:card", "summary_large_image");
  }, [isOpen, title, description, mediaUrl]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleShareClick = (platformId: string) => {
    setJustShared(platformId);
    setTimeout(() => setJustShared(null), 2000);
  };

  const labels = {
    share:
      locale === "ar" ? "مشاركة" : locale === "ku" ? "هاوبەشکردن" : "Share",
    shareProduct:
      locale === "ar"
        ? "مشاركة المنتج"
        : locale === "ku"
          ? "هاوبەشکردنی بەرهەم"
          : "Share Product",
    shareWith:
      locale === "ar"
        ? "شارك عبر"
        : locale === "ku"
          ? "هاوبەش بکە لە ڕێگای"
          : "Share via",
    copyLink:
      locale === "ar"
        ? "نسخ الرابط"
        : locale === "ku"
          ? "لینکەکە لەبەرگرتنەوە"
          : "Copy Link",
    copied:
      locale === "ar"
        ? "تم النسخ!"
        : locale === "ku"
          ? "لەبەرگیرایەوە!"
          : "Copied!",
    shared:
      locale === "ar"
        ? "تمت المشاركة!"
        : locale === "ku"
          ? "هاوبەش کرا!"
          : "Shared!",
  };

  const shareText = title
    ? `${labels.shareProduct}: ${title}`
    : labels.share;

  const getPlatformName = (platform: (typeof SHARE_PLATFORMS)[0]) => {
    return locale === "ar"
      ? platform.nameAr
      : locale === "ku"
        ? platform.nameKu
        : platform.nameEn;
  };

  const renderShareButton = (
    platform: (typeof SHARE_PLATFORMS)[0],
    index: number,
  ) => {
    const Icon = platform.icon;
    const isShared = justShared === platform.id;

    const buttonContent = (
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 + index * 0.06, duration: 0.35 }}
        onClick={() => handleShareClick(platform.id)}
        className={`group relative flex flex-col items-center gap-2.5 p-4 rounded-2xl border border-transparent hover:border-primary/10 ${platform.bg} hover:shadow-lg transition-all duration-300 cursor-pointer`}
      >
        {/* Glow effect on hover */}
        <div
          className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${platform.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}
        />

        <div className="relative">
          <div
            className={`flex size-14 items-center justify-center rounded-2xl bg-gradient-to-br ${platform.color} shadow-lg group-hover:scale-110 group-hover:shadow-xl transition-all duration-300`}
          >
            {isShared ? (
              <motion.div
                initial={{ scale: 0, rotate: -90 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", damping: 12 }}
              >
                <Check className="size-6 text-[#EAE4DB]" />
              </motion.div>
            ) : (
              <Icon className="size-6 text-[#EAE4DB]" />
            )}
          </div>

          {/* Success ring animation */}
          <AnimatePresence>
            {isShared && (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1.4, opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className={`absolute inset-0 rounded-2xl border-2 ${platform.iconColor.replace("text-", "border-")}`}
              />
            )}
          </AnimatePresence>
        </div>

        <span className="relative text-xs font-bold text-primary/80 group-hover:text-primary transition-colors">
          {isShared ? labels.shared : getPlatformName(platform)}
        </span>
      </motion.div>
    );

    switch (platform.id) {
      case "whatsapp":
        return (
          <WhatsappShareButton
            key={platform.id}
            url={url}
            title={title}
            className="!flex-1 !p-0"
          >
            {buttonContent}
          </WhatsappShareButton>
        );
      case "facebook":
        return (
          <FacebookShareButton
            key={platform.id}
            url={url}
            className="!flex-1 !p-0"
          >
            {buttonContent}
          </FacebookShareButton>
        );
      case "telegram":
        return (
          <TelegramShareButton
            key={platform.id}
            url={url}
            title={title}
            className="!flex-1 !p-0"
          >
            {buttonContent}
          </TelegramShareButton>
        );
      case "twitter":
        return (
          <TwitterShareButton
            key={platform.id}
            url={url}
            title={title}
            className="!flex-1 !p-0"
          >
            {buttonContent}
          </TwitterShareButton>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {renderTrigger(() => setIsOpen(true))}

      {isMounted &&
        createPortal(
          <AnimatePresence>
            {isOpen && (
              <>
                {/* Backdrop */}
                <motion.div
                  className="fixed inset-0 z-[80] bg-elegantBlack/60 backdrop-blur-md"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setIsOpen(false)}
                />

                {/* Centered container */}
                <motion.div
                  className="fixed inset-0 z-[90] grid place-items-center p-4 pointer-events-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <motion.div
                    className="bg-mainBg rounded-3xl p-6 md:p-7 w-full max-w-md shadow-[0_30px_80px_rgba(10,10,30,0.4)] pointer-events-auto border border-primary/8 overflow-hidden"
                    initial={{ scale: 0.85, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.85, opacity: 0, y: 20 }}
                    transition={{
                      type: "spring",
                      damping: 22,
                      stiffness: 260,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Header */}
                    <div className="flex justify-between items-center mb-6">
                      <div className="flex items-center gap-2.5">
                        <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10">
                          <Sparkles className="size-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-base md:text-lg font-bold text-primary">
                            {title ? shareText : labels.share}
                          </h3>
                          <p className="text-xs text-primary/50">
                            {labels.shareWith}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setIsOpen(false)}
                        className="size-9 rounded-xl bg-primary/5 hover:bg-primary/10 flex items-center justify-center transition-colors"
                      >
                        <X className="size-4.5 text-primary/60" />
                      </button>
                    </div>

                    {/* Product preview card (if mediaUrl/title) */}
                    {(mediaUrl || title) && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                        className="mb-6 rounded-full bg-[#F4EFEA] border border-[#E5DEC9] p-2 pr-4 rtl:pr-2 rtl:pl-4 flex items-center gap-3 shadow-[0_4px_12px_rgba(0,0,0,0.03)]"
                      >
                        {mediaUrl && (
                          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden border-[3px] border-white shadow-sm shrink-0 bg-white flex items-center justify-center">
                            <img
                              src={mediaUrl}
                              alt={title || ""}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src =
                                  "/images/logo.png";
                              }}
                            />
                          </div>
                        )}
                        <div className="min-w-0 flex-1 py-1">
                          {title && (
                            <p className="text-sm sm:text-base font-bold text-[#1E1E1E] truncate font-['PlutoRegular']">
                              {title}
                            </p>
                          )}
                          {description && (
                            <p className="text-[10px] sm:text-xs text-[#8B7E66] line-clamp-1 mt-0.5">
                              {description}
                            </p>
                          )}
                        </div>
                        {/* Decorative subtle icon on the right */}
                        <div className="shrink-0 w-8 h-8 rounded-full bg-[#E5DEC9] flex items-center justify-center mr-1 rtl:mr-0 rtl:ml-1 hidden sm:flex">
                          <Share2 className="w-4 h-4 text-[#4E82B4]" />
                        </div>
                      </motion.div>
                    )}

                    {/* Share platforms grid */}
                    <div className="grid grid-cols-4 gap-2 mb-6">
                      {SHARE_PLATFORMS.map((platform, index) =>
                        renderShareButton(platform, index),
                      )}
                    </div>

                    {/* Copy URL section */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.35 }}
                      className="flex items-center gap-2 bg-bodyBg rounded-xl px-3 py-2.5 border border-primary/8"
                    >
                      <span
                        className="flex-1 text-xs text-primary/60 truncate font-mono"
                        dir="ltr"
                      >
                        {url}
                      </span>
                      <motion.button
                        onClick={handleCopy}
                        whileTap={{ scale: 0.92 }}
                        className={`shrink-0 px-3.5 py-2 rounded-lg text-xs font-bold transition-all duration-300 flex items-center gap-1.5 ${
                          copied
                            ? "bg-emerald-500 text-[#EAE4DB]"
                            : "bg-primary text-mainBg hover:bg-primary/85"
                        }`}
                      >
                        {copied ? (
                          <>
                            <Check className="size-3.5" />
                            {labels.copied}
                          </>
                        ) : (
                          <>
                            <Copy className="size-3.5" />
                            {labels.copyLink}
                          </>
                        )}
                      </motion.button>
                    </motion.div>
                  </motion.div>
                </motion.div>
              </>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
};
